import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const INTERACTIVE_SELECTOR = "a, button, [data-magnetic], input, textarea, select, [role='button']";

// Desktop-only glowing cursor that scales up over interactive elements.
// No-ops entirely on touch devices, narrow viewports, or reduced-motion.
export const CustomCursor = () => {
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const springX = useSpring(x, { stiffness: 420, damping: 40 });
  const springY = useSpring(y, { stiffness: 420, damping: 40 });

  useEffect(() => {
    const isFinePointer = window.matchMedia("(pointer: fine)").matches;
    const isWideViewport = window.innerWidth >= 1024;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!isFinePointer || !isWideViewport || reduceMotion) return;

    setEnabled(true);
    document.body.classList.add("custom-cursor-active");

    const move = (e) => { x.set(e.clientX); y.set(e.clientY); };
    const over = (e) => { if (e.target.closest(INTERACTIVE_SELECTOR)) setHovering(true); };
    const out = (e) => { if (e.target.closest(INTERACTIVE_SELECTOR)) setHovering(false); };

    window.addEventListener("mousemove", move);
    document.addEventListener("mouseover", over);
    document.addEventListener("mouseout", out);
    return () => {
      document.body.classList.remove("custom-cursor-active");
      window.removeEventListener("mousemove", move);
      document.removeEventListener("mouseover", over);
      document.removeEventListener("mouseout", out);
    };
  }, [x, y]);

  if (!enabled) return null;

  return (
    <motion.div
      aria-hidden="true"
      style={{ x: springX, y: springY }}
      className="pointer-events-none fixed left-0 top-0 z-[70] will-change-transform"
    >
      <div className="-translate-x-1/2 -translate-y-1/2">
        <motion.span
          animate={{ scale: hovering ? 2.2 : 1, opacity: hovering ? 0.9 : 0.7 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="block h-3 w-3 rounded-full bg-brand-accent shadow-glow-primary"
        />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <motion.span
            animate={{ scale: hovering ? 1.6 : 1, opacity: hovering ? 0.5 : 0.22 }}
            transition={{ duration: 0.3 }}
            className="block h-9 w-9 rounded-full border border-brand-accent/40"
          />
        </div>
      </div>
    </motion.div>
  );
};
